import { z } from 'zod';

import { agentRunIdSchema } from '@apps/web/components/phoebe_chat/agent_run_id';

const dynamicWidgetModeSchema = z.enum(['live', 'sandbox']);

export {
    agentRunIdSchema,
    isValidAgentRunId,
} from '@apps/web/components/phoebe_chat/agent_run_id';

export const dynamicWidgetEventSourceSchema = z
    .object({
        kind: z.literal('event_source'),
        toolName: z.string(),
        callId: z.string(),
        toolCallEventId: z.string(),
        toolOutputEventId: z.string().nullable(),
        phase: z.enum(['arguments', 'output']),
        capturedAt: z.string(),
        organizationId: z.string(),
        mode: dynamicWidgetModeSchema,
    })
    .readonly();

export type DynamicWidgetEventSource = z.infer<
    typeof dynamicWidgetEventSourceSchema
>;

export const dynamicWidgetResourceSourceSchema = z
    .object({
        kind: z.literal('resource_source'),
        resourceType: z.string(),
        resourceId: z.string(),
        apiPath: z.string(),
        // Null until a poll lands. An adapter builds from committed events
        // only, so it can name the resource a card reads but never a fetch
        // time; the renderer owns the live fetch clock and its "As of" label.
        fetchedAt: z.string().nullable(),
        organizationId: z.string(),
        mode: dynamicWidgetModeSchema,
    })
    .readonly();

export type DynamicWidgetResourceSource = z.infer<
    typeof dynamicWidgetResourceSourceSchema
>;

export const dynamicWidgetSourceSchema = z.discriminatedUnion('kind', [
    dynamicWidgetEventSourceSchema,
    dynamicWidgetResourceSourceSchema,
]);

export type DynamicWidgetSource = z.infer<typeof dynamicWidgetSourceSchema>;

export const dynamicWidgetBindingPresentationSchema = z
    .object({
        section: z.string().min(1),
        rowKey: z.string().min(1).nullable(),
        label: z.string().min(1),
        role: z.enum(['field', 'control']),
    })
    .readonly();

export type DynamicWidgetBindingPresentation = z.infer<
    typeof dynamicWidgetBindingPresentationSchema
>;

export function buildBindingPresentation({
    section,
    rowKey = null,
    label,
    role = 'field',
}: {
    readonly section: string;
    readonly rowKey?: string | null;
    readonly label: string;
    readonly role?: DynamicWidgetBindingPresentation['role'];
}): DynamicWidgetBindingPresentation {
    return { section, rowKey, label, role };
}

export const dynamicWidgetBindingSchema = z
    .object({
        id: z.string(),
        kind: z.enum(['source', 'calculated', 'copy', 'live']),
        sourceIds: z.array(z.string()).readonly(),
        paths: z.array(z.string()).readonly(),
        transform: z.string().nullable(),
        rawValue: z.unknown(),
        renderedValue: z.string().nullable(),
        freshness: z.enum(['current', 'stale', 'unknown']),
        presentation: dynamicWidgetBindingPresentationSchema,
    })
    .readonly();

export type DynamicWidgetBinding = z.infer<typeof dynamicWidgetBindingSchema>;

export const dynamicWidgetIssueSchema = z
    .object({
        id: z.string(),
        code: z.enum([
            'source_invalid',
            'source_missing',
            'source_stale',
            'permission_denied',
            'action_rejected',
            'action_failed',
            'live_refresh_failed',
            'render_failed',
            'unsupported',
        ]),
        level: z.enum(['info', 'warning', 'error']),
        scope: z.enum(['field', 'section', 'action', 'widget']),
        bindingIds: z.array(z.string()).readonly(),
        sourceIds: z.array(z.string()).readonly(),
        retryable: z.boolean(),
        safeDetails: z.record(
            z.string(),
            z.union([z.string(), z.number(), z.boolean(), z.null()]),
        ),
    })
    .readonly();

export type DynamicWidgetIssue = z.infer<typeof dynamicWidgetIssueSchema>;

const dynamicWidgetBaseShape = {
    widgetId: z.string(),
    schemaVersion: z.literal(1),
    templateVersion: z.number().int().positive(),
    sources: z.array(dynamicWidgetSourceSchema).readonly(),
    bindings: z.array(dynamicWidgetBindingSchema).readonly(),
    issues: z.array(dynamicWidgetIssueSchema).readonly(),
};

export const dynamicWidgetBaseSchema = z
    .object(dynamicWidgetBaseShape)
    .readonly();

export type DynamicWidgetBase = z.infer<typeof dynamicWidgetBaseSchema>;

export const shiftListWidgetRowSchema = z
    .object({
        shiftId: z.string(),
        patientName: z.string(),
        day: z.string().nullable(),
        timeLabel: z.string().nullable(),
        city: z.string().nullable(),
        status: z.string().nullable(),
        frequency: z.string().nullable(),
        caregiverId: z.string().nullable(),
        caregiverName: z.string().nullable(),
        seriesOpen: z.number().nullable(),
        seriesCovered: z.number().nullable(),
        roleRequired: z.string().nullable(),
        skillsRequired: z.array(z.string()).readonly(),
        futureOccurrenceIds: z.array(z.string()).readonly(),
        occurrenceIds: z.array(z.string()).readonly(),
        outreachState: z
            .object({
                active: z.boolean(),
                paused: z.boolean(),
                byStatus: z.record(z.string(), z.number().int().nonnegative()),
                byType: z.record(z.string(), z.number().int().nonnegative()),
                latestStatus: z.string().nullable(),
                latestType: z.string().nullable(),
            })
            .readonly()
            .optional(),
    })
    .readonly();

export type ShiftListWidgetRow = z.infer<typeof shiftListWidgetRowSchema>;

export const shiftListWidgetDayGroupSchema = z
    .object({
        day: z.string().nullable(),
        dayLabel: z.string(),
        rowIndexes: z.array(z.number().int().nonnegative()).readonly(),
    })
    .readonly();

export type ShiftListWidgetDayGroup = z.infer<
    typeof shiftListWidgetDayGroupSchema
>;

const shiftListWidgetModelShape = {
    ...dynamicWidgetBaseShape,
    template: z.literal('shifts'),
    rows: z.array(shiftListWidgetRowSchema).readonly(),
    dayGroups: z.array(shiftListWidgetDayGroupSchema).readonly(),
    note: z.string().nullable(),
    rowCount: z.number().int().nonnegative(),
    openShiftCount: z.number().int().nonnegative(),
    bulkOpenShiftCount: z.number().int().nonnegative(),
    bulkShiftIds: z.array(z.string()).readonly(),
};

export const shiftListWidgetModelV1Schema = z
    .object({
        ...shiftListWidgetModelShape,
        templateVersion: z.literal(1),
    })
    .readonly();

export type ShiftListWidgetModelV1 = z.infer<
    typeof shiftListWidgetModelV1Schema
>;

export const resourceShiftListWidgetModelSchema = z
    .object({
        ...shiftListWidgetModelShape,
        templateVersion: z.literal(3),
        showHeaderCount: z.boolean(),
        workspaceFile: z
            .object({
                runId: agentRunIdSchema,
                path: z.string().min(1),
            })
            .readonly(),
        rowProvenance: z.enum(['event_preview', 'resource']),
    })
    .readonly();

export type ResourceShiftListWidgetModel = z.infer<
    typeof resourceShiftListWidgetModelSchema
>;

export const shiftListWidgetModelSchema = z.discriminatedUnion(
    'templateVersion',
    [shiftListWidgetModelV1Schema, resourceShiftListWidgetModelSchema],
);

export type ShiftListWidgetModel = z.infer<typeof shiftListWidgetModelSchema>;

/**
 * Live outreach progress card (T2). The model carries only the creation facts
 * frozen in the committed `draft_agent_outreach` output; status, counts, the
 * progress rail, and the per-caregiver rows are polled from the outreach and
 * contact-attempt reads named in `sources`.
 */
export const outreachProgressWidgetModelSchema = z
    .object({
        ...dynamicWidgetBaseShape,
        template: z.literal('progress'),
        templateVersion: z.literal(1),
        outreachId: z.string().min(1),
        outreachType: z.string().nullable(),
        shiftIds: z.array(z.string()).readonly(),
        scheduledStartAt: z.string().nullable(),
        recurrenceDescription: z.string().nullable(),
    })
    .readonly();

export type OutreachProgressWidgetModel = z.infer<
    typeof outreachProgressWidgetModelSchema
>;

export const caregiverListWidgetRowSchema = z
    .object({
        caregiverId: z.string().nullable(),
        displayName: z.string().nullable(),
        active: z.boolean().nullable(),
        roles: z.array(z.string()).readonly(),
        office: z.string().nullable(),
        city: z.string().nullable(),
        qualificationTags: z.array(z.string()).readonly(),
        valid: z.boolean(),
        // Ranked-match fields (T3 mockup), present only when the rows come
        // from the recommendation pipeline rather than a directory read.
        rank: z.number().int().nullable().default(null),
        shiftId: z.string().nullable().default(null),
        distanceMiles: z.number().nullable().default(null),
        weeklyHours: z.number().nullable().default(null),
        desiredWeeklyHours: z.number().nullable().default(null),
        previousVisits: z.number().int().nullable().default(null),
        available: z.boolean().nullable().default(null),
        preferred: z.boolean().default(false),
        warnings: z.array(z.string()).readonly().default([]),
    })
    .readonly();

export type CaregiverListWidgetRow = z.infer<
    typeof caregiverListWidgetRowSchema
>;

export const caregiverListWidgetModelSchema = z
    .object({
        ...dynamicWidgetBaseShape,
        template: z.literal('entities'),
        templateVersion: z.literal(1),
        // 'directory' renders a plain caregiver listing; 'ranked' renders
        // the T3 "Top caregiver matches" surface with rank, selection, and
        // outreach actions.
        variant: z.enum(['directory', 'ranked']).default('directory'),
        rows: z.array(caregiverListWidgetRowSchema).readonly(),
        rowCount: z.number().int().nonnegative(),
        rowProvenance: z.enum(['event_preview', 'resource']),
        workspaceFile: z
            .object({
                runId: agentRunIdSchema,
                path: z.string().min(1),
            })
            .readonly(),
    })
    .readonly();

export type CaregiverListWidgetModel = z.infer<
    typeof caregiverListWidgetModelSchema
>;

export const outreachHistoryWidgetRowSchema = z
    .object({
        outreachId: z.string().nullable(),
        outreachType: z.string().nullable(),
        attemptId: z.string().nullable(),
        caregiverId: z.string().nullable(),
        caregiverName: z.string().nullable(),
        shiftId: z.string().nullable(),
        shiftStartTime: z.string().nullable(),
        shiftEndTime: z.string().nullable(),
        channel: z.string().nullable(),
        outreachCreatedAt: z.string().nullable(),
        responseAt: z.string().nullable(),
        attemptStatus: z.string().nullable(),
        status: z.string().nullable(),
        outcome: z.string().nullable(),
        valid: z.boolean(),
    })
    .readonly();

export type OutreachHistoryWidgetRow = z.infer<
    typeof outreachHistoryWidgetRowSchema
>;

export const outreachHistoryWidgetModelSchema = z
    .object({
        ...dynamicWidgetBaseShape,
        template: z.literal('outreach_history'),
        templateVersion: z.literal(1),
        rows: z.array(outreachHistoryWidgetRowSchema).readonly(),
        rowCount: z.number().int().nonnegative(),
        rowProvenance: z.enum(['event_preview', 'resource']),
        organizationTimezone: z.string().min(1),
        workspaceFile: z
            .object({
                runId: agentRunIdSchema,
                path: z.string().min(1),
            })
            .readonly(),
    })
    .readonly();

export type OutreachHistoryWidgetModel = z.infer<
    typeof outreachHistoryWidgetModelSchema
>;

export const clientDetailWidgetRecordSchema = z
    .object({
        clientId: z.string().nullable(),
        displayName: z.string().nullable(),
        preferredName: z.string().nullable(),
        office: z.string().nullable(),
        city: z.string().nullable(),
        state: z.string().nullable(),
        tags: z.array(z.string()).readonly(),
        active: z.boolean().nullable(),
        valid: z.boolean(),
    })
    .readonly();

export type ClientDetailWidgetRecord = z.infer<
    typeof clientDetailWidgetRecordSchema
>;

export const clientDetailWidgetModelSchema = z
    .object({
        ...dynamicWidgetBaseShape,
        template: z.literal('detail'),
        templateVersion: z.literal(1),
        record: clientDetailWidgetRecordSchema.nullable(),
        rowCount: z.literal(1),
        rowProvenance: z.enum(['event_preview', 'resource']),
        workspaceFile: z
            .object({
                runId: agentRunIdSchema,
                path: z.string().min(1),
            })
            .readonly(),
    })
    .readonly();

export type ClientDetailWidgetModel = z.infer<
    typeof clientDetailWidgetModelSchema
>;

export const genericTableWidgetModelSchema = z
    .object({
        ...dynamicWidgetBaseShape,
        template: z.literal('table'),
        templateVersion: z.literal(1),
        columns: z.array(z.string()).readonly(),
        columnsTruncated: z.boolean(),
        rows: z.array(z.record(z.string(), z.unknown())).readonly(),
        rowCount: z.number().int().nonnegative(),
        byteSize: z.number().int().nonnegative(),
        format: z.enum(['csv', 'jsonl']),
        suggestedComponent: z
            .enum(['shifts', 'caregivers', 'client_detail'])
            .nullable(),
        rowProvenance: z.literal('resource'),
        workspaceFile: z
            .object({
                organizationId: z.string().min(1),
                runId: agentRunIdSchema,
                path: z.string().min(1),
                eventId: z.string().min(1),
            })
            .readonly(),
    })
    .readonly();

export type GenericTableWidgetModel = z.infer<
    typeof genericTableWidgetModelSchema
>;

const dynamicWidgetModelMembers = [
    shiftListWidgetModelSchema,
    outreachProgressWidgetModelSchema,
    caregiverListWidgetModelSchema,
    outreachHistoryWidgetModelSchema,
    clientDetailWidgetModelSchema,
    genericTableWidgetModelSchema,
] as const;

export const dynamicWidgetModelSchema = z.union(dynamicWidgetModelMembers);

export type DynamicWidgetModel = z.infer<typeof dynamicWidgetModelSchema>;

type SourceFieldOptions<T> = {
    readonly id: string;
    readonly sourceId: string;
    readonly path: string;
    readonly rawValue: unknown;
    readonly value: T;
    readonly renderedValue: string | null;
    readonly freshness?: DynamicWidgetBinding['freshness'];
    readonly presentation: DynamicWidgetBindingPresentation;
};

type CalculatedFieldOptions<T> = {
    readonly id: string;
    readonly sourceIds: ReadonlyArray<string>;
    readonly paths: ReadonlyArray<string>;
    readonly transform: string;
    readonly rawValue: unknown;
    readonly value: T;
    readonly renderedValue: string | null;
    readonly freshness?: DynamicWidgetBinding['freshness'];
    readonly presentation: DynamicWidgetBindingPresentation;
};

/**
 * Adds model values and their bindings together, so adapters cannot attach a
 * dynamic value without recording its provenance at the same call site.
 */
export class DynamicWidgetModelBuilder {
    private readonly sources: ReadonlyArray<DynamicWidgetSource>;
    private readonly bindings: Array<DynamicWidgetBinding> = [];

    public constructor(sources: ReadonlyArray<DynamicWidgetSource>) {
        this.sources = sources;
    }

    public addSourceField<T>(options: SourceFieldOptions<T>): T {
        this.bindings.push(
            dynamicWidgetBindingSchema.parse({
                id: options.id,
                kind: 'source',
                sourceIds: [options.sourceId],
                paths: [options.path],
                transform: null,
                rawValue: options.rawValue,
                renderedValue: options.renderedValue,
                freshness: options.freshness ?? 'current',
                presentation: options.presentation,
            }),
        );
        return options.value;
    }

    public addCalculatedField<T>(options: CalculatedFieldOptions<T>): T {
        this.bindings.push(
            dynamicWidgetBindingSchema.parse({
                id: options.id,
                kind: 'calculated',
                sourceIds: options.sourceIds,
                paths: options.paths,
                transform: options.transform,
                rawValue: options.rawValue,
                renderedValue: options.renderedValue,
                freshness: options.freshness ?? 'current',
                presentation: options.presentation,
            }),
        );
        return options.value;
    }

    /**
     * Declares a value the renderer reads straight from a polled resource, so
     * the model still records where every dynamic field comes from. The value
     * itself lives in the SWR cache rather than the committed model, so it has
     * no raw or rendered value here and its freshness is unknown until a poll
     * lands.
     */
    public addLiveField(options: {
        readonly id: string;
        readonly sourceIds: ReadonlyArray<string>;
        readonly paths: ReadonlyArray<string>;
        readonly presentation: DynamicWidgetBindingPresentation;
    }): void {
        this.bindings.push(
            dynamicWidgetBindingSchema.parse({
                id: options.id,
                kind: 'live',
                sourceIds: options.sourceIds,
                paths: options.paths,
                transform: null,
                rawValue: null,
                renderedValue: null,
                freshness: 'unknown',
                presentation: options.presentation,
            }),
        );
    }

    public buildBase({
        widgetId,
        templateVersion,
        issues,
    }: {
        readonly widgetId: string;
        readonly templateVersion: number;
        readonly issues: ReadonlyArray<DynamicWidgetIssue>;
    }): DynamicWidgetBase {
        return dynamicWidgetBaseSchema.parse({
            widgetId,
            schemaVersion: 1,
            templateVersion,
            sources: this.sources,
            bindings: this.bindings,
            issues,
        });
    }
}

export function getDynamicWidgetSourceId(source: DynamicWidgetSource): string {
    switch (source.kind) {
        case 'event_source':
            return `event:${source.toolCallEventId}:${source.phase}`;
        case 'resource_source':
            return `resource:${source.resourceType}:${source.resourceId}`;
        default: {
            const exhaustiveSource: never = source;
            return exhaustiveSource;
        }
    }
}

export function buildDynamicWidgetId(
    toolCallEventId: string,
    template: DynamicWidgetModel['template'],
): string {
    return `${toolCallEventId}:${template}`;
}
